import React from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tooltip from '../../../../shared/components/Tooltip'
import {
	faEnvelope,
	faShoePrints,
	faPhone,
	faGlobeAmericas,
	faChartLine,
} from '@fortawesome/free-solid-svg-icons'
import {
	checkIsMobile,
	getLanguageLocale,
	stringifyNum,
	getCurrency,
	getCurrencyInfo,
	sortString,
} from 'shared'
import { formatToStringLocale } from 'shared/utils/dateAndTimeHelpers'
import { useTransactions } from '../../../hooks/useTransactions'
import { SeeAll } from '../SeeAll/SeeAll'
import { Select } from '../Select/Select'
import { CardItem } from '../CardItem/CardItem'
import { ITransaction } from 'selfServicePortal/interfaces/i-api-creaditCard'
import Table, { ITableHeader } from 'selfServicePortal/components/Table/Table'
import './CardTransactions.scss'
import './extendedTransactions.scss'
import Map from '../Map/Map'

interface ICardTransactions {
	isOverview: boolean
}

const CardTransactions = ({ isOverview }: ICardTransactions) => {
	const { t } = useTranslation()
	const isMobile = checkIsMobile()

	const { getMoreTransactionsFromMonth, tableData, hideShowMore } =
		useTransactions()

	const getTableData = (item: ITransaction) => {
		const consumerClarity = item.consumerClarity

		const transactionProps = {
			lat: consumerClarity?.merchantResult?.merchantLocation?.latitude,
			long: consumerClarity?.merchantResult?.merchantLocation?.longitude,
			imgUrl: consumerClarity?.merchantResult?.logos?.[0]?.url,
			imgAltText: consumerClarity?.merchantResult?.logos?.[0]?.altTextTag,
			merchantName: consumerClarity?.merchantResult?.merchantName,
			description: consumerClarity?.merchantResult?.description,
			carbonGrams:
				consumerClarity?.carbonFootprint?.carbonEmissionInGrams,
			carbonOunces:
				consumerClarity?.carbonFootprint?.carbonEmissionInOunces,
			website: consumerClarity?.merchantResult?.websiteUrl,
			phone: consumerClarity?.merchantResult?.phone,
			email: consumerClarity?.merchantResult?.email,
		}

		return {
			merchantLogo: transactionProps.imgUrl ? (
				<img
					src={transactionProps.imgUrl}
					alt={transactionProps.imgAltText}
					className="transaction-logo-sm"
				/>
			) : (
				<div className="transaction-logo-sm circle-logo" />
			),
			merchantName:
				consumerClarity?.merchantResult?.merchantName ?? item.vendor,
			bookingDate: item.isBlockedAuthorization
				? ''
				: new Date(item.bookingDate).toLocaleDateString(
						getLanguageLocale(),
				  ),
			transactionDate: new Date(item.transactionDate).toLocaleDateString(
				getLanguageLocale(),
			),
			description:
				item.isBlockedAuthorization && item.description === 'None'
					? t('AuthorizationDescription')
					: item.description,
		}
	}

	const renderSubRow = (dataRow: ITransaction) => {
		const consumerClarity = dataRow.consumerClarity
		const contact =
			consumerClarity?.merchantResult?.email ||
			consumerClarity?.merchantResult?.websiteUrl ||
			consumerClarity?.merchantResult?.phone
		const phone = consumerClarity?.merchantResult?.phone
		const email = consumerClarity?.merchantResult?.email
		const websiteUrl = consumerClarity?.merchantResult?.websiteUrl
		const currencyInfo = getCurrencyInfo(dataRow?.currency)
		const desc = dataRow.description
		const description = desc.substring(0, 15)

		if (!dataRow) return <></>
		return (
			<>
				<div className="row m-4 clarityContainer">
					<div className="rowContainer">
						<CardItem
							content={[
								'Amount',
								'BillingAmount',
								'Currency',
								'TransactionType',
							]}
							value={[
								stringifyNum(dataRow.amount, {
									currency: dataRow.currency!.toString(),
									style: 'currency',
								})!,
								stringifyNum(dataRow.billingAmount, {
									currency:
										dataRow.billingCurrency!.toString(),
									style: 'currency',
								})!,
								currencyInfo,
								dataRow.transactionType,
							]}
						/>
						<CardItem
							title={t('CurrencyInformation')}
							icon={faChartLine}
							content={['ExchangeRate', 'BillingCurrency']}
							value={[
								dataRow.exchangeRate!.toString(),
								dataRow.currency!.toString(),
							]}
						/>
					</div>
					<div className=" rowContainer">
						<CardItem
							content={['BookingDate', 'TransactionDate']}
							value={[
								formatToStringLocale(dataRow.bookingDate),
								formatToStringLocale(dataRow.transactionDate),
							]}
						/>
						<CardItem
							content={[
								'Description',
								dataRow.consumerClarity?.merchantResult
									?.merchantCategory?.description
									? 'Category'
									: '',
							]}
							value={[
								description?.toLowerCase(),
								dataRow.consumerClarity?.merchantResult?.merchantCategory?.description?.toLowerCase() ||
									'',
							]}
						/>
						{dataRow.consumerClarity?.carbonFootprint
							?.carbonEmissionInGrams &&
							dataRow.consumerClarity?.carbonFootprint
								?.carbonEmissionInOunces && (
								<CardItem
									title={t('CarbonFootprint')}
									icon={faShoePrints}
									content={[
										'CarbonEmissionPerGram',
										'CarbonEmissionPerOunces',
									]}
									value={[
										dataRow.consumerClarity?.carbonFootprint?.carbonEmissionInGrams?.toString() ??
											'',
										dataRow.consumerClarity?.carbonFootprint?.carbonEmissionInOunces?.toString() ??
											'',
									]}
									inlineIconStyle={{
										transform:
											'matrix(0.87, -0.5, 0.5, 0.87, 0, 0)',
									}}
								/>
							)}
					</div>
					<div className="rowContainer">
						{dataRow.consumerClarity?.merchantResult
							?.merchantLocation?.longitude &&
							dataRow.consumerClarity?.merchantResult
								?.merchantLocation?.latitude && (
								<CardItem
									title={t('Location')}
									specialContent={[
										<Map
											lat={
												dataRow.consumerClarity
													?.merchantResult
													?.merchantLocation.latitude
											}
											long={
												dataRow.consumerClarity
													?.merchantResult
													?.merchantLocation.longitude
											}
										/>,
									]}
								/>
							)}
						{contact && (
							<CardItem
								title={t('Contact')}
								specialContent={[
									email ? (
										<div>
											<span className="pr-2">
												<FontAwesomeIcon
													icon={faEnvelope}
												/>
											</span>
											{email}
										</div>
									) : (
										<></>
									),
									phone ? (
										<div>
											<span className="pr-2">
												<FontAwesomeIcon
													icon={faPhone}
												/>
											</span>
											{phone}
										</div>
									) : (
										<></>
									),
									websiteUrl ? (
										<div>
											<span className="pr-2">
												<FontAwesomeIcon
													icon={faGlobeAmericas}
												/>
											</span>
											{websiteUrl}
										</div>
									) : (
										<></>
									),
								]}
							/>
						)}
					</div>
				</div>
			</>
		)
	}

	const TableHeaders: ITableHeader<ITransaction>[] = [
		{
			// only for mobile. Its not neat but it is what it is
			accessor: 'consumerClarity',
			header: '',
			show: isMobile,
			Cell: ({ original }) => {
				const data = getTableData(original)
				const merchantName = isMobile
					? data.merchantName.substring(0, 25)
					: data.merchantName
				return (
					<div>
						<div className="merchant-info-container">
							<div className="merchant-logo">
								{data.merchantLogo}
							</div>
							<div className="merchant-name">{merchantName}</div>
						</div>
						<div className="transaction-summary-mobile">
							<div className="transaction-date">
								{data.transactionDate}
							</div>
							<div className="transaction-summary-mobile">
								<div>{`${stringifyNum(
									original.billingAmount,
								)} ${original.billingCurrency}`}</div>
							</div>
						</div>
					</div>
				)
			},
		},
		{
			accessor: 'consumerClarity',
			header: t('Merchant'),
			show: !isMobile,
			Cell: ({ original }) => {
				const data = getTableData(original)
				const merchantName = data.merchantName
				return (
					<div className="merchant-info-container">
						<div className="merchant-logo">{data.merchantLogo}</div>
						<div className="merchant-name">{merchantName}</div>
					</div>
				)
			},
			Sort: (sort) => {
				if (sort === 'asc') {
					tableData.sort((a, b) =>
						sortString(
							getTableData(a).merchantName,
							getTableData(b).merchantName,
							'asc',
						),
					)
				} else {
					tableData.sort((a, b) =>
						sortString(
							getTableData(a).merchantName,
							getTableData(b).merchantName,
							'desc',
						),
					)
				}
			},
		},
		{
			accessor: 'transactionDate',
			header: t('TransactionDate'),
			show: !isMobile,
			Cell: ({ original }) => {
				const data = getTableData(original)
				return <div>{data.transactionDate}</div>
			},
			Sort: (sort) => {
				if (sort === 'asc') {
					tableData.sort((a, b) =>
						sortString(a.transactionDate, b.transactionDate, 'asc'),
					)
				} else {
					tableData.sort((a, b) =>
						sortString(
							a.transactionDate,
							b.transactionDate,
							'desc',
						),
					)
				}
			},
		},
		{
			accessor: 'description',
			header: t('Description'),
			show: !isMobile,
			Cell: ({ original }) => {
				const data = getTableData(original)
				return data.description
			},
		},
		{
			accessor: 'billingAmount',
			header: t('BillingAmount'),
			show: !isMobile,
			align: 'right',
			Cell: ({ original }) => {
				return `${stringifyNum(original.amount)} ${original.currency}`
			},
			Sort: (sort) => {
				if (sort === 'asc') {
					tableData.sort((a, b) => a.billingAmount - b.billingAmount)
				} else {
					tableData.sort((a, b) => b.billingAmount - a.billingAmount)
				}
			},
		},
		{
			accessor: 'invoiceAmount',
			header: t('InvoiceAmount'),
			show: !isMobile,
			align: 'right',
			Cell: ({ original }) => {
				return (
					<div>{`${stringifyNum(original.invoiceAmount)} ${
						original.billingCurrency
					}`}</div>
				)
			},
			Sort: (sort) => {
				if (sort === 'asc') {
					tableData.sort((a, b) => a.invoiceAmount - b.invoiceAmount)
				} else {
					tableData.sort((a, b) => b.invoiceAmount - a.invoiceAmount)
				}
			},
		},
		{
			accessor: 'isExpanded' as unknown as keyof ITransaction, //this should really be in the table itself
			header: '',
			Cell: (row) => {
				return (
					<FontAwesomeIcon
						icon={row.isExpanded ? 'chevron-up' : 'chevron-down'}
						role="button"
					/>
				)
			},
		},
	]
	return (
		<div className="container mt-2 mb-4">
			<div className="transaction-title">
				{t('Transactions')}

				{!isOverview && (
					<Tooltip
						item={{
							value: t('Infotext'),
						}}
					/>
				)}
			</div>
			<div className="fade-in transactions outer-container">
				{!isOverview && (
					<div className="transaction-search-container">
						<Select
							getMoreTransactionsFromMonth={
								getMoreTransactionsFromMonth
							}
						/>
					</div>
				)}

				{tableData.length >= 1 ? (
					<>
						<Table
							columns={TableHeaders}
							data={tableData as ITransaction[]}
							subData={renderSubRow}
						/>
						<div className="d-flex justify-content-center">
							<SeeAll
								isOverview={isOverview}
								showMore={() =>
									getMoreTransactionsFromMonth(
										tableData[0].transactionDate,
										tableData.length + 10,
									)
								}
								hideShowMore={hideShowMore}
							/>
						</div>
					</>
				) : (
					<p>{t('NoMonthTransactions')}</p>
				)}
			</div>
		</div>
	)
}

export default CardTransactions
